import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone, MapPin, MessageSquare, Send, Loader2 } from "lucide-react";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Fly Local Boost",
    "description": "Get in touch with Fly Local Boost for local SEO and Google Business Profile management support.",
    "url": "https://fly-social.com/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Fly Local Boost",
      "url": "https://fly-social.com",
      "email": "fly@zoomlocal.in",
      "telephone": "+919270362196",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Pune",
        "addressRegion": "Maharashtra",
        "addressCountry": "IN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+919270362196",
        "contactType": "customer service",
        "availableLanguage": ["English"]
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast({ title: "Required fields missing", description: "Please fill in your name and email.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-contact-form', { body: formData });
      if (error) throw error;
      toast({ title: "Message sent!", description: "We'll get back to you within 24 hours." });
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error: any) {
      console.error('Contact form error:', error);
      toast({ title: "Failed to send message", description: "Please try again or contact us directly.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>Contact Us - Fly Local Boost</title>
        <meta name="description" content="Get in touch with Fly Local Boost. We're here to help you dominate local search and grow your business." />
        <meta name="keywords" content="contact, support, help, local seo, google business profile" />
        <link rel="canonical" href="https://fly-social.com/contact" />
        <meta property="og:title" content="Contact Fly Local Boost - Local SEO Support" />
        <meta property="og:description" content="Get in touch with Fly Local Boost. We're here to help you dominate local search and grow your business." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fly-social.com/contact" />
        <meta property="og:image" content="https://fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Fly Local Boost - Local SEO Support" />
        <meta name="twitter:description" content="Get in touch with Fly Local Boost. We're here to help you dominate local search and grow your business." />
        <script type="application/ld+json">{JSON.stringify(contactPageSchema)}</script>
      </Helmet>

      {/* Stripped header — sticky Fly logo only, no nav links. Same
          single-funnel pattern as the lead-gen-tools landing and the
          agency pages. Logo uses natural colors here because the
          Contact page hero is a light gradient (not the dark theme
          used by the landing pages). */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-10 pt-6 pointer-events-none">
        <a href="/" className="inline-flex items-center pointer-events-auto" aria-label="Fly — Contact">
          <img
            src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
            alt="Fly"
            className="h-16 md:h-20 drop-shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
          />
        </a>
      </div>

      {/* Hero */}
      <section
        className="pt-24 md:pt-32 pb-16 relative overflow-hidden dark:bg-slate-950"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center">
          <RevealOnScroll>
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 mb-6 rounded-full text-sm font-semibold border dark:border-purple-700/50 dark:text-purple-300 dark:bg-purple-900/30"
              style={{
                background: `linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(16,185,129,0.1) 100%)`,
                borderColor: `${LANDING_COLORS.primary}30`,
                color: LANDING_COLORS.primary,
              }}
            >
              <Mail className="h-4 w-4" />
              GET IN TOUCH
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4 dark:text-white"
              style={{ color: LANDING_COLORS.text.primary }}
            >
              We'd Love to{' '}
              <span style={{ color: LANDING_COLORS.primary }}>Hear From You</span>
            </h1>
            <p
              className="text-lg md:text-xl max-w-2xl mx-auto dark:text-slate-400"
              style={{ color: LANDING_COLORS.text.secondary }}
            >
              Have questions about our platform? Need help with your local SEO strategy? We're here to help.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-start">

            {/* Contact Form */}
            <RevealOnScroll>
              <div className="bg-white dark:bg-slate-800/60 rounded-2xl shadow-lg dark:shadow-slate-900/50 p-8 border border-gray-100 dark:border-slate-700/50">
                <h2 className="text-2xl font-bold mb-6 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>
                  Send us a message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">Name *</Label>
                      <Input id="name" name="name" placeholder="Your name" value={formData.name} onChange={handleChange} required className="dark:bg-slate-700/50 dark:border-slate-600 dark:text-white dark:placeholder-gray-400" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email *</Label>
                      <Input id="email" name="email" type="email" placeholder="your@email.com" value={formData.email} onChange={handleChange} required className="dark:bg-slate-700/50 dark:border-slate-600 dark:text-white dark:placeholder-gray-400" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-700 dark:text-gray-300">Company</Label>
                      <Input id="company" name="company" placeholder="Your company" value={formData.company} onChange={handleChange} className="dark:bg-slate-700/50 dark:border-slate-600 dark:text-white dark:placeholder-gray-400" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 dark:text-gray-300">Phone</Label>
                      <Input id="phone" name="phone" type="tel" placeholder="+1 (555) 000-0000" value={formData.phone} onChange={handleChange} className="dark:bg-slate-700/50 dark:border-slate-600 dark:text-white dark:placeholder-gray-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-700 dark:text-gray-300">Message</Label>
                    <Textarea id="message" name="message" placeholder="How can we help you?" rows={5} value={formData.message} onChange={handleChange} className="dark:bg-slate-700/50 dark:border-slate-600 dark:text-white dark:placeholder-gray-400" />
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white font-semibold"
                    style={{ background: `linear-gradient(135deg, ${LANDING_COLORS.primary} 0%, ${LANDING_COLORS.primaryLight} 100%)` }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="mr-2 h-4 w-4" />Send Message</>
                    )}
                  </Button>
                </form>
              </div>
            </RevealOnScroll>

            {/* Contact Info */}
            <RevealOnScroll delay={1}>
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-6 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>
                    Other ways to reach us
                  </h2>
                  <div className="space-y-5">
                    {[
                      { icon: Mail, label: "Email", value: "fly@zoomlocal.in", href: "mailto:fly@zoomlocal.in", sub: "We reply within 24 hours", color: LANDING_COLORS.primary, bg: `${LANDING_COLORS.primary}15` },
                      { icon: MessageSquare, label: "WhatsApp", value: "+91 92703 62196", href: "https://wa.me/919270362196", sub: "Quick responses during business hours", color: "#25D366", bg: "rgba(37,211,102,0.1)" },
                      { icon: Phone, label: "Phone", value: "+91 92703 62196", href: "tel:+919270362196", sub: "Mon-Fri, 10am - 6pm IST", color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
                      { icon: MapPin, label: "Office", value: "Pune, Maharashtra, India", href: null, sub: null, color: "#f97316", bg: "rgba(249,115,22,0.1)" },
                    ].map(({ icon: Icon, label, value, href, sub, color, bg }) => (
                      <div key={label} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center dark:bg-slate-700/50" style={{ backgroundColor: bg }}>
                          <Icon className="h-5 w-5" style={{ color }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{label}</h3>
                          {href ? (
                            <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="hover:underline" style={{ color }}>
                              {value}
                            </a>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-400">{value}</p>
                          )}
                          {sub && <p className="text-sm text-gray-500 dark:text-gray-500 mt-0.5">{sub}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQ */}
                <div>
                  <h3 className="font-bold text-lg mb-4 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>Frequently Asked</h3>
                  <Accordion type="single" collapsible className="space-y-3">
                    {[
                      { value: "respond", q: "How quickly do you respond?", a: "We typically respond within 24 hours on business days." },
                      { value: "demo", q: "Do you offer demos?", a: "Yes! Request a demo and we'll walk you through the platform." },
                      { value: "trial", q: "Is there a free trial?", a: "Yes, we offer a free trial with no credit card required." },
                    ].map(({ value, q, a }) => (
                      <AccordionItem key={value} value={value} className="border-0">
                        <Card className="group border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all duration-300">
                          <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                            <span className="text-sm font-semibold text-left group-hover:text-purple-700 dark:text-slate-100 dark:group-hover:text-purple-400 transition-colors">
                              {q}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-5 pb-4">
                            <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">{a}</p>
                          </AccordionContent>
                        </Card>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            </RevealOnScroll>

          </div>
        </div>
      </section>

      <Footer compact />
    </div>
  );
};

export default Contact;
